import {
  any,
  compose,
  concat,
  equals,
  filter,
  find,
  flatten,
  map,
  mapObjIndexed,
  path,
  pathOr,
  pipe,
  prop,
  propEq,
  startsWith,
  toLower,
  values
} from "ramda";
import localForage from "localforage";
import { createAlimaarayksetBEObjects } from "@/helpers/rajoitteetHelper";
import {
  createMaarayksiaVastenLuodutRajoitteetBEObjects,
  getRajoitteetByValue
} from "@/utils/rajoitteetUtils";
import { getMaarayksetByTunniste } from "@/helpers/lupa";
import { PaikallisenTietovarastonAvain } from "@/enums";
import { luoMuutosobjektitLisatietokentasta } from "@/helpers/lisatiedot";

function setOpiskelijamaara(opiskelijamaaraChangeObj) {
  const opiskelijamaara = path(["properties", "value"], opiskelijamaaraChangeObj);
  if (!opiskelijamaaraChangeObj) return 0;
  if (Number.isNaN(opiskelijamaara)) return 0;
  return opiskelijamaara;
}

function setKuvaus(kuvausChangeObj) {
  const kuvaus = path(["properties", "value"], kuvausChangeObj);
  if (!kuvaus) return "englanti";
  return kuvaus;
}

function setValikko(changeObj, opiskelijamaaraChangeObj, kuvausChangeObj) {
  if (changeObj) return path(["properties", "metadata", "valikko"], changeObj);
  if (opiskelijamaaraChangeObj) return path(["properties", "metadata", "lkm"], opiskelijamaaraChangeObj);
  if (kuvausChangeObj) return path(["properties", "metadata", "kuvaus"], kuvausChangeObj);
}

export const defineBackendChangeObjects = async (
  changeObjects = {},
  kohde,
  maaraystyypit,
  lupaMaaraykset,
  kohteet
) => {
  const maaraykset = await getMaarayksetByTunniste(
    kohde.tunniste,
    lupaMaaraykset
  );
  const opetuskielet = await localForage.getItem(
    PaikallisenTietovarastonAvain.ENSISIJAISET_OPETUSKIELET_OPH
  );

  const { rajoitteetByRajoiteId } = changeObjects;

  const opetuskieletChangeObjs = filter(
    compose(startsWith("opetuskielet.opetuskieli."), prop("anchor")),
    changeObjects.opetuskielet
  );

  const lukioKohde = find(propEq("tunniste", "opetuskieli"), kohteet);
  const vstKohde = find(propEq("tunniste", "kielet"), kohteet);
  const kohdeObj = lukioKohde || vstKohde;

  const valitutEnsisijaisetKoodiarvot = pipe(
    find(obj => obj.anchor?.endsWith(".ensisijaiset")),
    pathOr([], ["properties", "value"]),
    map(prop("value"))
  )(opetuskieletChangeObjs);

  const isEnglantiSelected =
    (maaraykset?.some(maarays => maarays.koodiarvo === "en") && valitutEnsisijaisetKoodiarvot?.length === 0) ||
    (valitutEnsisijaisetKoodiarvot?.includes("EN") ?? false);

  const opiskelijamaaraChangeObj = isEnglantiSelected ? find(
    compose(
      startsWith("opetuskielet.opetuskieli-englanti.ensisijaiset.lkm"),
      prop("anchor")
    ),
    changeObjects.opetuskielet
  ) : null;

  const kuvausChangeObj = isEnglantiSelected ? find(
    compose(startsWith("opetuskielet.opetuskieli-englanti.ensisijaiset.kuvaus"), prop("anchor")),
    changeObjects.opetuskielet
  ) : null;

  let opiskelijamaara;
  let kuvaus;
  if (lukioKohde && isEnglantiSelected) {
    opiskelijamaara = setOpiskelijamaara(opiskelijamaaraChangeObj);
    kuvaus = setKuvaus(kuvausChangeObj);
  }
  // Lisätietokentän muutosten käsittely
  const lisatietomuutokset = await luoMuutosobjektitLisatietokentasta(
    kohde,
    changeObjects.opetuskielet
  );

  /** Opetuskielten käsittely */
  const opetuskieliBeChangeObjects = map(opetuskieli => {
    const changeObj = find(
      cObj =>
        find(
          kieli => kieli.value === opetuskieli.koodiarvo,
          cObj.properties.value
        ),
      opetuskieletChangeObjs
    );

    const isCObjAndOpetuskieliEnglanti = changeObj?.properties?.value?.some(item => item.value === "EN")
      && opetuskieli.koodiarvo === "EN" || false;

    const rajoitteetByRajoiteIdAndKoodiarvo = getRajoitteetByValue(
      opetuskieli.koodiarvo,
      rajoitteetByRajoiteId
    );

    const maarays = find(
      maarays =>
        maarays.koodiarvo.toLowerCase() === opetuskieli.koodiarvo.toLowerCase(),
      maaraykset
    );

    const isMaaraysAndOpetuskieliEnglanti = maarays?.koodiarvo === "en"
      && opetuskieli.koodiarvo === "EN" || false;

    const isValikkoChanged = any(change => {
      return equals(
        path(["properties", "metadata", "valikko"], change),
        path(["meta", "valikko"], maarays)
      );
    }, opetuskieletChangeObjs);

    // Luodaan lisäys...
    // jos on kokonaan uusi kielivalinta
    // jos on tullut muutos englannin opiskelijamäärä- tai kuvaus-kenttiin
    const lisaysObjekti = (changeObj && !maarays ||
      opetuskieli.koodiarvo === "EN" && opiskelijamaaraChangeObj ||
      opetuskieli.koodiarvo === "EN" && kuvausChangeObj) ?
      {
        generatedId: `opetuskielet-${Math.random()}`,
        kohde: kohdeObj,
        koodiarvo: toLower(opetuskieli.koodiarvo),
        koodisto: opetuskieli.koodisto.koodistoUri,
        maaraystyyppi: find(propEq("tunniste", "OIKEUS"), maaraystyypit),
        arvo: (isMaaraysAndOpetuskieliEnglanti || isCObjAndOpetuskieliEnglanti) && opiskelijamaara != null ? opiskelijamaara : null,
        meta: {
          kuvaus: isMaaraysAndOpetuskieliEnglanti || isCObjAndOpetuskieliEnglanti && kuvaus ? kuvaus : null,
          changeObjects: concat(
            [
              changeObj,
              ...(isMaaraysAndOpetuskieliEnglanti || isCObjAndOpetuskieliEnglanti
                ? [opiskelijamaaraChangeObj, kuvausChangeObj]
                : [])
            ].filter(Boolean),
            values(rajoitteetByRajoiteIdAndKoodiarvo)
          ),
          ...(changeObj || opiskelijamaaraChangeObj || kuvausChangeObj
            ? {
              valikko: setValikko(changeObj, opiskelijamaaraChangeObj, kuvausChangeObj)
            }
            : null)
        },
        tila: "LISAYS"
      } : null;

    // Luodaan poisto...
    // jos määräys on olemassa ja valikko on muuttunut,
    // jos kyseessä on englanti ja opiskelijamäärä tai kuvaus on muuttunut
    // jos englanti poistetaan kokonaan
    const poistoObjekti = (maarays && !changeObj && isValikkoChanged ||
      isMaaraysAndOpetuskieliEnglanti && opiskelijamaaraChangeObj ||
      isMaaraysAndOpetuskieliEnglanti && kuvausChangeObj ||
      isMaaraysAndOpetuskieliEnglanti && !isEnglantiSelected) ?
      {
        generatedId: `opetuskielet-${Math.random()}`,
        kohde: kohdeObj,
        koodiarvo: toLower(opetuskieli.koodiarvo),
        koodisto: opetuskieli.koodisto.koodistoUri,
        maaraystyyppi: find(propEq("tunniste", "OIKEUS"), maaraystyypit),
        arvo: isMaaraysAndOpetuskieliEnglanti ? maarays.arvo : null,
        meta: {
          kuvaus: isMaaraysAndOpetuskieliEnglanti ? maarays.meta?.kuvaus : null,
          changeObjects: opetuskieletChangeObjs,
          ...(changeObj || opiskelijamaaraChangeObj || kuvausChangeObj
            ? {
              valikko: setValikko(changeObj, opiskelijamaaraChangeObj, kuvausChangeObj)
            }
            : null)
        },
        maaraysUuid: maarays?.uuid,
        tila: "POISTO"
      } : null;

    // Muodostetaan tehdyistä rajoituksista objektit backendiä varten.
    // Linkitetään ensimmäinen rajoitteen osa yllä luotuun muutokseen ja
    // loput toisiinsa "alenevassa polvessa".
    const alimaaraykset = lisaysObjekti
      ? values(
          mapObjIndexed(asetukset => {
            return createAlimaarayksetBEObjects(
              kohteet,
              maaraystyypit,
              lisaysObjekti,
              asetukset
            );
          }, rajoitteetByRajoiteIdAndKoodiarvo)
        )
      : [];

    return [lisaysObjekti, poistoObjekti, alimaaraykset].filter(Boolean);
  }, opetuskielet);

  const maarayksiaVastenLuodutRajoitteet =
    createMaarayksiaVastenLuodutRajoitteetBEObjects(
      maaraykset,
      rajoitteetByRajoiteId,
      kohteet,
      maaraystyypit,
      kohde
    );

  return flatten([
    opetuskieliBeChangeObjects,
    lisatietomuutokset,
    maarayksiaVastenLuodutRajoitteet
  ]).filter(Boolean);
};
